/**
 * Accessibility patches for the ReturnGO returns portal (/pages/return-and-exchange).
 *
 * The page body embeds `<div id="ReturngoPortalEmbedding">` plus ReturnGO's
 * script; ReturnGO renders a React app into an OPEN shadow root on
 * `#ReturngoPortalShadowDOM` inside that div. SortSite reads through the
 * shadow root, and the 28 IX scan flagged there:
 *  1. `<a>` elements used as buttons with no href ("Returning a gift?", "find
 *     my order", menu and header icons): no role and not focusable (F54, F15).
 *     They get role="button" and tabindex="0", and Enter/Space fire a click,
 *     which is what the React handlers listen to. A disabled one ("find my
 *     order" before the form is filled in) also gets aria-disabled.
 *  2. The "?" icon next to Order Number is `<span role="button">` with only an
 *     SVG inside: a button with no name (4.1.2).
 *  3. Each input sits in its own `<fieldset>` with no `<legend>` (H71). As on
 *     the Enzuzo form, a `display: none` first-child legend repeats the
 *     field's label text, which is what SortSite checks for. display:none
 *     rather than sr-only: SortSite counts clipped sr-only text as blank (see
 *     SwatchHelperToA11y.js). A display:none legend doesn't name the group in
 *     the accessibility tree, so the fieldset also points aria-labelledby at
 *     it (hidden text still counts when it's referenced).
 *
 * The page body used to carry an older inline `patchReturnGO` script doing
 * 1 and 2 (and an sr-only legend), but it only started on window `load` and
 * then polled, so the scanner saw the widget before it ran; it was removed
 * from the page on 1 X 2026. This runs from DOMContentLoaded and patches from
 * MutationObserver callbacks, i.e. before the browser paints the vendor's
 * render. Keyboard handlers are still bound through the expando that script
 * checked (`__rgKeyboardFixApplied`), so pasting it back in can't make Enter
 * click twice.
 */

const EMBED_SELECTOR = "#ReturngoPortalEmbedding";
const HOST_SELECTOR = "#ReturngoPortalShadowDOM";
const FIRST_LEGEND_MARKER_ATTR = "data-a11y-rg-legend";
const LEGEND_ID_PREFIX = "a11y-rg-legend-";
const DISABLED_MARKER_ATTR = "data-a11y-rg-disabled";
const DISABLED_CLASS_PATTERN = /-module_disabled\b/;
// Natively focusable and keyboard-operable already: named if blank, otherwise left alone.
const NATIVE_CONTROL_TAGS = new Set(["BUTTON", "INPUT", "SELECT", "TEXTAREA"]);
// The expando the old inline patchReturnGO script checked; see above.
const KEYBOARD_BOUND_EXPANDO = "__rgKeyboardFixApplied";
// How long to wait for ReturnGO to attach the shadow root once the host exists.
const SHADOW_ROOT_WAIT_FRAMES = 120;

// Names for the vendor's icon-only controls, matched by their CSS module class.
const ICON_BUTTON_NAMES = [
    ["ReturnGO_PreviewBanner-module_iconBtn", () => "Close preview banner"],
    ["ReturnGO_InputField-module_tooltipIconWrapper", (element) => moreInformationName(element)],
    ["ReturnGO_Menu-module_toggle", () => "Open menu"],
    ["ReturnGO_Header-module_control", (element) => (element.querySelector(".ReturnGO_Close-module_icon") ? "Close" : "Back")],
];
const FALLBACK_BUTTON_NAME = "Button";

let legendIdSequence = 0;
const watchedEmbeds = new WeakSet();
const waitingHosts = new WeakSet();
const observedRoots = new WeakSet();

export const patchReturnGoA11y = () => {
    const embed = document.querySelector(EMBED_SELECTOR);
    if (!embed) {
        return;
    }

    attachToHost(embed);

    if (watchedEmbeds.has(embed)) {
        return;
    }
    watchedEmbeds.add(embed);

    const observer = new MutationObserver(() => attachToHost(embed));
    observer.observe(embed, { childList: true, subtree: true });
};

const attachToHost = (embed) => {
    const host = embed.querySelector(HOST_SELECTOR);
    if (!host) {
        return;
    }
    if (host.shadowRoot) {
        observeRoot(host.shadowRoot);
        return;
    }
    waitForShadowRoot(host);
};

// attachShadow() doesn't produce a mutation the light-DOM observer can see, so
// if the host shows up before its shadow root, check again for a few frames.
const waitForShadowRoot = (host) => {
    if (waitingHosts.has(host)) {
        return;
    }
    waitingHosts.add(host);

    let framesLeft = SHADOW_ROOT_WAIT_FRAMES;
    const check = () => {
        if (host.shadowRoot) {
            waitingHosts.delete(host);
            observeRoot(host.shadowRoot);
            return;
        }
        framesLeft -= 1;
        if (framesLeft > 0) {
            requestAnimationFrame(check);
            return;
        }
        waitingHosts.delete(host);
    };
    requestAnimationFrame(check);
};

const observeRoot = (root) => {
    patchRoot(root);

    if (observedRoots.has(root)) {
        return;
    }
    observedRoots.add(root);

    // `class` so aria-disabled follows the vendor enabling/disabling a button.
    const observer = new MutationObserver(() => patchRoot(root));
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
};

const patchRoot = (root) => {
    addFieldsetLegends(root);
    // An href-less link with a role of its own (tab, menuitem, ...) is left to that role.
    root.querySelectorAll("a:not([href]):not([role])").forEach((link) => link.setAttribute("role", "button"));
    root.querySelectorAll('[role="button"]').forEach(patchButton);
};

// Only writes when something is missing or stale, so the observer that re-runs
// this after our own changes settles instead of looping.
const addFieldsetLegends = (root) => {
    root.querySelectorAll("fieldset").forEach((fieldset) => {
        const vendorLegend = Array.from(fieldset.querySelectorAll("legend")).find(
            (legend) => !legend.hasAttribute(FIRST_LEGEND_MARKER_ATTR)
        );
        if (vendorLegend) {
            return;
        }

        const label = fieldset.querySelector("label");
        const text = label ? label.textContent.replace(/\s+/g, " ").trim() : "";
        if (!text) {
            return;
        }

        let legend = fieldset.querySelector(`:scope > legend[${FIRST_LEGEND_MARKER_ATTR}]`);
        if (!legend) {
            legend = document.createElement("legend");
            legend.setAttribute(FIRST_LEGEND_MARKER_ATTR, "");
            legend.hidden = true;
            legend.style.display = "none";
        }
        if (legend.textContent !== text) {
            legend.textContent = text;
        }
        if (fieldset.firstElementChild !== legend) {
            fieldset.insertBefore(legend, fieldset.firstChild);
        }
        nameFieldsetByLegend(fieldset, legend);
    });
};

// Leaves a fieldset the vendor names itself alone.
const nameFieldsetByLegend = (fieldset, legend) => {
    if (!legend.id) {
        legend.id = `${LEGEND_ID_PREFIX}${legendIdSequence++}`;
    }
    const labelledBy = fieldset.getAttribute("aria-labelledby");
    if (labelledBy === legend.id) {
        return;
    }
    if (labelledBy || fieldset.hasAttribute("aria-label")) {
        return;
    }
    fieldset.setAttribute("aria-labelledby", legend.id);
};

const patchButton = (element) => {
    nameIconButton(element);
    if (NATIVE_CONTROL_TAGS.has(element.tagName)) {
        return;
    }

    syncAriaDisabled(element);

    if (!element.hasAttribute("tabindex")) {
        element.setAttribute("tabindex", "0");
    }

    if (element[KEYBOARD_BOUND_EXPANDO]) {
        return;
    }
    element.addEventListener("keydown", (event) => {
        // Only keys pressed on the button itself: not on a field inside it, not auto-repeat.
        if (event.target !== element || event.repeat || (event.key !== "Enter" && event.key !== " ")) {
            return;
        }
        event.preventDefault();
        if (element.getAttribute("aria-disabled") === "true") {
            return;
        }
        element.click();
    });
    element[KEYBOARD_BOUND_EXPANDO] = true;
};

// Follows the vendor's disabled class. Marks the aria-disabled we add, so a
// vendor-set aria-disabled is never removed.
const syncAriaDisabled = (element) => {
    const disabled = DISABLED_CLASS_PATTERN.test(element.className);
    const addedByUs = element.hasAttribute(DISABLED_MARKER_ATTR);
    if (disabled && !element.hasAttribute("aria-disabled")) {
        element.setAttribute("aria-disabled", "true");
        element.setAttribute(DISABLED_MARKER_ATTR, "");
    } else if (!disabled && addedByUs) {
        element.removeAttribute("aria-disabled");
        element.removeAttribute(DISABLED_MARKER_ATTR);
    }
};

const nameIconButton = (element) => {
    const hasText = element.textContent.trim() !== "";
    if (hasText || element.getAttribute("aria-label") || element.getAttribute("aria-labelledby")) {
        return;
    }
    const className = typeof element.className === "string" ? element.className : "";
    const match = ICON_BUTTON_NAMES.find(([cls]) => className.includes(cls));
    element.setAttribute("aria-label", match ? match[1](element) : FALLBACK_BUTTON_NAME);
};

const moreInformationName = (icon) => {
    const label = icon.closest("fieldset")?.querySelector("label");
    const text = label ? label.textContent.replace(/\s+/g, " ").trim() : "";
    return text ? `More information about ${text}` : "More information";
};
